import { z } from 'zod';
import type { OwnerScope } from '@prisma/client';
import type { ConfigChangeSummary } from './provenance-summary.js';

export const TAG_SCOPES = ['PRIVATE', 'HUB'] as const;
export type TagScope = typeof TAG_SCOPES[number];

export const TAG_GROUP_SELECTION_MODES = ['SINGLE', 'MULTI'] as const;
export type TagGroupSelectionMode = typeof TAG_GROUP_SELECTION_MODES[number];

export const tagScopeSchema = z.enum(TAG_SCOPES);
export const tagGroupSelectionModeSchema = z.enum(TAG_GROUP_SELECTION_MODES);

export const tagCreateSchema = z.object({
  name: z.string().trim().min(1),
  description: z.string().trim().min(1).optional().nullable(),
  scope: tagScopeSchema.default('PRIVATE'),
  tagGroupId: z.number().int().positive().optional().nullable(),
  tagOrder: z.number().int().optional().nullable(),
}).strict();

export type TagCreateInput = z.infer<typeof tagCreateSchema>;

export const tagUpdateSchema = z.object({
  name: z.string().trim().min(1).optional(),
  description: z.string().trim().min(1).optional().nullable(),
  tagGroupId: z.number().int().positive().optional().nullable(),
  tagOrder: z.number().int().optional().nullable(),
}).strict().refine((input) => {
  return input.name !== undefined
    || input.description !== undefined
    || input.tagGroupId !== undefined
    || input.tagOrder !== undefined;
}, {
  message: 'At least one field must be provided',
});

export type TagUpdateInput = z.infer<typeof tagUpdateSchema>;

export const tagReorderSchema = z.object({
  tagIds: z.array(z.number().int().positive()).superRefine((tagIds, ctx) => {
    if (new Set(tagIds).size !== tagIds.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'tagIds must not contain duplicate IDs',
      });
    }
  }),
  tagGroupId: z.number().int().positive().optional().nullable(),
}).strict();

export type TagReorderInput = z.infer<typeof tagReorderSchema>;

export interface Tag {
  id: number;
  createdByMemberId: number;
  ownerMemberId: number | null;
  hubId: number;
  name: string;
  description?: string;
  ownerScope: OwnerScope;
  tagGroupId?: number;
  tagOrder?: number;
  isSystem: boolean;
  memberAssignable: boolean;
  createdAt: string;
  updatedAt: string;
  lastChange?: ConfigChangeSummary | null;
}

export interface TagAssignAutomationSummary {
  matchedProcessCount: number;
  createdProcessRunCount: number;
  enqueueAttemptedCount: number;
  enqueueSucceededCount: number;
  enqueueFailedCount: number;
  processRunIds: number[];
  failedEnqueueProcessRunIds: number[];
  enqueueFailures: Array<{
    processRunId: number;
    error: string;
  }>;
}

export interface TagAssignResponse {
  tag: Tag;
  automation: TagAssignAutomationSummary;
}
